"use client";

import { useEffect, useRef } from "react";
import { useWatch } from "react-hook-form";
import { Loader2, X } from "@/components/icons";
import { useAppForm } from "@/components/form/use-app-form";
import { ActionErrorAlert } from "@/components/ui/action-error-alert";
import { Button } from "@/components/ui/button";
import { ChipSelect } from "@/components/ui/chip-select";
import { trackContactFormView } from "@/lib/analytics/gtm-lead-metadata";
import { handoffMessageFill } from "@/lib/support/support-handoff-text";
import { contactPageFormDefaultValues } from "@/lib/validation/defaults";
import { contactPageFormSchema, INQUIRY_TYPES, inquiryTypes } from "@/lib/validation/schemas";
import { isOneOf } from "@shared/type-guards";
import { ContactFormConsent } from "./contact-form-consent";
import { ContactFormFields, type ContactFormApi } from "./contact-form-fields";
import { useSupportHandoff, type SupportHandoff } from "./support-handoff";
import { useContactFormPrefill } from "./use-contact-form-prefill";
import { useContactFormSubmit } from "./use-contact-form-submit";

function HandoffNotice({ handoff, onRemove }: { handoff: SupportHandoff; onRemove: () => void }) {
  return (
    <div className="flex items-start gap-3 rounded-lg bg-action-tint px-4 py-3">
      <span className="mt-1.5 size-2 shrink-0 rounded-full bg-action-fill" aria-hidden />
      <p className="min-w-0 flex-1 text-sm text-foreground">
        We&apos;ll attach your question and the answer you got: &ldquo;{handoff.question}&rdquo;
      </p>
      <Button type="button" variant="quiet" size="compact" onClick={onRemove} aria-label="Don't attach the conversation">
        <X />
        Remove
      </Button>
    </div>
  );
}

function TopicPicker({ form }: { form: ContactFormApi }) {
  const inquiryType = useWatch({ control: form.control, name: "inquiryType" });
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">What is this about?</p>
      <ChipSelect
        aria-label="What is this about?"
        value={inquiryType}
        options={INQUIRY_TYPES}
        onValueChange={(next) => {
          if (isOneOf(inquiryTypes, next)) form.setValue("inquiryType", next);
        }}
      />
    </div>
  );
}

function HoneypotField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="absolute -left-[9999px] opacity-0" aria-hidden="true">
      <label htmlFor="contact-website">Website</label>
      <input
        id="contact-website"
        type="text"
        name="website"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        tabIndex={-1}
        autoComplete="off"
      />
    </div>
  );
}

export function ContactForm() {
  const prefill = useContactFormPrefill();
  const { handoff, setHandoff } = useSupportHandoff();
  const { submit, submitError, honeypot, setHoneypot } = useContactFormSubmit(prefill, handoff);
  const form = useAppForm({
    schema: contactPageFormSchema,
    defaultValues: { ...contactPageFormDefaultValues, inquiryType: prefill.inquiryType, message: prefill.message },
  });
  const isSubmitting = form.formState.isSubmitting;

  useEffect(() => {
    trackContactFormView("contact-form");
  }, []);

  const lastHandoffFill = useRef<string | null>(null);
  useEffect(() => {
    if (!handoff) return;
    const fill = handoffMessageFill(form.getValues("message"), lastHandoffFill.current, handoff.question);
    if (fill === null) return;
    form.setValue("message", fill);
    lastHandoffFill.current = fill;
  }, [handoff, form]);

  return (
    <form onSubmit={form.handleSubmit(submit)} noValidate className="space-y-6">
      {handoff && <HandoffNotice handoff={handoff} onRemove={() => setHandoff(null)} />}
      <TopicPicker form={form} />
      <ContactFormFields form={form} />
      <HoneypotField value={honeypot} onChange={setHoneypot} />
      <ContactFormConsent form={form} />
      {submitError && <ActionErrorAlert error={submitError} />}
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="animate-spin motion-reduce:animate-none" />}
          {isSubmitting ? "Sending…" : "Send message"}
        </Button>
      </div>
    </form>
  );
}
